import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import {
  accountService,
  driverService,
  scoreService,
  userRatingService,
  DriverDto,
  AccountDto,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { exportCsv } from '../utils/exportCsv'
import VerificationReviewDialog from '../components/VerificationReviewDialog'
import ConfirmDialog from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'
import SubjectDocuments from '../components/SubjectDocuments'
import ColumnVisibilityMenu, { ColumnDef } from '../components/ColumnVisibilityMenu'
import { usePersistedTableState } from '../utils/usePersistedTableState'

const DRIVER_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Name', alwaysVisible: true },
  { id: 'licenseNo', label: 'License No' },
  { id: 'licenseExpiry', label: 'License Expiry' },
  { id: 'kyc', label: 'KYC' },
  { id: 'userStatus', label: 'User Status' },
  { id: 'account', label: 'Account' },
  { id: 'rating', label: 'Rating' },
  { id: 'actions', label: 'Actions', alwaysVisible: true },
]

const PAGE_SIZE = 20
const KYC_STATUSES = ['PENDING', 'VERIFIED', 'REJECTED', 'EXPIRED', 'SUSPENDED']

function kycChipColor(s?: string): 'success' | 'warning' | 'error' | 'default' {
  if (s === 'VERIFIED') return 'success'
  if (s === 'REJECTED' || s === 'EXPIRED') return 'error'
  if (s === 'PENDING' || s === 'SUSPENDED') return 'warning'
  return 'default'
}

function daysUntil(dateStr?: string): number | null {
  if (!dateStr) return null
  const ms = new Date(dateStr).getTime() - Date.now()
  return Math.floor(ms / 86400000)
}

function expiryChip(dateStr?: string) {
  const d = daysUntil(dateStr)
  if (d == null) return <Typography variant="caption" color="text.secondary">—</Typography>
  const dateLabel = new Date(dateStr as string).toLocaleDateString()
  if (d < 0) return <Chip label={`${dateLabel} (expired)`} color="error" size="small" />
  if (d <= 30) return <Chip label={`${dateLabel} (${d}d)`} color="warning" size="small" />
  return <Chip label={dateLabel} color="default" size="small" variant="outlined" />
}

export default function DriversPage() {
  const [page, setPage] = useState(0)
  const [accountFilter, setAccountFilter] = useState<string>('')
  const [kycFilter, setKycFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [expiryFilter, setExpiryFilter] = useState<'ALL' | 'EXPIRED' | 'SOON' | 'OK'>(
    'ALL'
  )

  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null)

  const [scoreDialogOpen, setScoreDialogOpen] = useState(false)
  const [scoreAdjustment, setScoreAdjustment] = useState(0)
  const [scoreReason, setScoreReason] = useState('')

  const [kycDialogOpen, setKycDialogOpen] = useState(false)
  const [kycVerificationId, setKycVerificationId] = useState<string | undefined>(undefined)
  const [kycInitiateParams, setKycInitiateParams] = useState<
    { subjectType: 'DRIVER'; subjectId: string; accountId: string } | undefined
  >(undefined)

  const [deleteConfirm, setDeleteConfirm] = useState<DriverDto | null>(null)
  const [resetRatingTarget, setResetRatingTarget] = useState<DriverDto | null>(null)

  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()
  const tableState = usePersistedTableState('drivers')

  // Cross-account paginated list
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-drivers', page, accountFilter],
    queryFn: () => driverService.getAll(page, PAGE_SIZE, accountFilter || undefined),
  })

  // Accounts for the filter dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-filter'],
    queryFn: () => accountService.getAll(0, 200),
  })
  const accounts: AccountDto[] = useMemo(
    () => accountsData?.content ?? accountsData ?? [],
    [accountsData]
  )
  const accountById = useMemo(() => {
    const m = new Map<string, AccountDto>()
    accounts.forEach((a) => m.set(a.id, a))
    return m
  }, [accounts])

  // Selected driver — fetched fresh by id when the dialog opens
  const { data: selectedDriver } = useQuery({
    queryKey: ['driver', selectedDriverId],
    queryFn: () => driverService.getById(selectedDriverId as string),
    enabled: viewDialogOpen && !!selectedDriverId,
  })

  const deleteMutation = useMutation({
    mutationFn: (driverId: string) => driverService.delete(driverId, false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-drivers'] })
      toast.success('Driver deleted')
      setDeleteConfirm(null)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to delete driver'),
  })

  const resetRatingMutation = useMutation({
    mutationFn: (userId: string) => userRatingService.resetUserRating(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-drivers'] })
      queryClient.invalidateQueries({ queryKey: ['driver', selectedDriverId] })
      toast.success('Rating reset')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reset rating'),
  })

  const adjustScoreMutation = useMutation({
    mutationFn: ({
      driverId,
      adjustment,
      reason,
    }: {
      driverId: string
      adjustment: number
      reason: string
    }) =>
      scoreService.adjustScore('DRIVER', driverId, { adjustment, reason }),
    onSuccess: () => {
      toast.success('Score adjusted')
      setScoreDialogOpen(false)
      setScoreAdjustment(0)
      setScoreReason('')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to adjust score'),
  })

  const drivers: DriverDto[] = useMemo(() => data?.content ?? [], [data])
  const totalPages: number = data?.totalPages ?? 0

  const filteredDrivers = useMemo(() => {
    return drivers.filter((d) => {
      if (kycFilter && d.kycVerificationStatus !== kycFilter) return false
      if (search) {
        const q = search.toLowerCase()
        const hay = [
          d.licenseNo,
          d.id,
          d.user?.name,
          d.user?.email,
          d.user?.phone,
        ]
        if (!hay.some((v) => v?.toLowerCase().includes(q))) return false
      }
      if (expiryFilter !== 'ALL') {
        const days = daysUntil(d.licenseExpiry)
        if (expiryFilter === 'EXPIRED' && (days == null || days >= 0)) return false
        if (expiryFilter === 'SOON' && (days == null || days < 0 || days > 30)) return false
        if (expiryFilter === 'OK' && (days == null || days <= 30)) return false
      }
      return true
    })
  }, [drivers, kycFilter, search, expiryFilter])

  const openDriver = (id: string) => {
    setSelectedDriverId(id)
    setViewDialogOpen(true)
  }

  const handleExport = () => {
    exportCsv<DriverDto>(`drivers-${new Date().toISOString().slice(0, 10)}`,
      [
        { header: 'ID', field: 'id' },
        { header: 'Name', field: (r) => r.user?.name || '' },
        { header: 'Email', field: (r) => r.user?.email || '' },
        { header: 'Phone', field: (r) => r.user?.phone || '' },
        { header: 'License No', field: 'licenseNo' },
        { header: 'License Expiry', field: 'licenseExpiry' },
        { header: 'KYC Status', field: 'kycVerificationStatus' },
        { header: 'User Status', field: (r) => r.user?.status || '' },
        { header: 'Account', field: 'accountId' },
        { header: 'Rating', field: (r) => r.user?.rating ?? '' },
        { header: 'Created', field: 'createdAt' },
      ],
      filteredDrivers)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
        spacing={2}
      >
        <Stack direction="row" spacing={1}>
          <ColumnVisibilityMenu
            columns={DRIVER_COLUMNS}
            hidden={tableState.hiddenColumns}
            onToggle={tableState.toggleColumn}
            onReset={tableState.resetColumns}
          />
          <Tooltip title="Export current view to CSV">
            <IconButton onClick={handleExport}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-drivers'] })}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Account"
            value={accountFilter}
            onChange={(e) => {
              setAccountFilter(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All accounts</MenuItem>
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.businessName || a.id.substring(0, 8)} ({a.type})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="KYC status"
            value={kycFilter}
            onChange={(e) => setKycFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {KYC_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {getLocalizedLabel('kycVerificationStatus', s) || s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="License"
            value={expiryFilter}
            onChange={(e) => setExpiryFilter(e.target.value as any)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
            <MenuItem value="SOON">≤ 30 days</MenuItem>
            <MenuItem value="OK">More than 30d</MenuItem>
          </TextField>
          <TextField
            size="small"
            placeholder="Search id / license / name / email / phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {isLoading ? (
        <Paper sx={{ flex: 1, minHeight: 0 }}><TableSkeleton rows={8} columns={DRIVER_COLUMNS.length - tableState.hiddenColumns.length} /></Paper>
      ) : isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load drivers"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table sx={{ height: filteredDrivers.length === 0 ? '100%' : undefined }}>
            <TableHead>
              <TableRow>
                {!tableState.isColumnHidden('name') && <ResizableTableCell>Name</ResizableTableCell>}
                {!tableState.isColumnHidden('licenseNo') && <ResizableTableCell>License No</ResizableTableCell>}
                {!tableState.isColumnHidden('licenseExpiry') && <ResizableTableCell>License Expiry</ResizableTableCell>}
                {!tableState.isColumnHidden('kyc') && <ResizableTableCell>KYC</ResizableTableCell>}
                {!tableState.isColumnHidden('userStatus') && <ResizableTableCell>User Status</ResizableTableCell>}
                {!tableState.isColumnHidden('account') && <ResizableTableCell>Account</ResizableTableCell>}
                {!tableState.isColumnHidden('rating') && <ResizableTableCell>Rating</ResizableTableCell>}
                <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDrivers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={DRIVER_COLUMNS.length - tableState.hiddenColumns.length} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title="No drivers match the current filters"
                      description="Adjust the account, KYC, expiry, or search filters above."
                    />
                  </TableCell>
                </TableRow>
              )}
              {filteredDrivers.map((driver) => {
                const acctId = driver.accountId ?? driver.user?.accountId
                const account = acctId ? accountById.get(acctId) : undefined
                return (
                  <TableRow
                    key={driver.id}
                    hover
                    onClick={() => openDriver(driver.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {!tableState.isColumnHidden('name') && (
                      <TableCell>
                        <Typography variant="body2">{driver.user?.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {driver.user?.email || driver.user?.phone || ''}
                        </Typography>
                      </TableCell>
                    )}
                    {!tableState.isColumnHidden('licenseNo') && (
                      <TableCell>{driver.licenseNo}</TableCell>
                    )}
                    {!tableState.isColumnHidden('licenseExpiry') && (
                      <TableCell>{expiryChip(driver.licenseExpiry)}</TableCell>
                    )}
                    {!tableState.isColumnHidden('kyc') && (
                      <TableCell>
                        <Chip
                          label={
                            getLocalizedLabel('kycVerificationStatus', driver.kycVerificationStatus || '') ||
                            driver.kycVerificationStatus ||
                            '—'
                          }
                          color={kycChipColor(driver.kycVerificationStatus)}
                          size="small"
                        />
                      </TableCell>
                    )}
                    {!tableState.isColumnHidden('userStatus') && (
                      <TableCell>
                        <Chip
                          label={
                            getLocalizedLabel('userStatus', driver.user?.status || '') ||
                            driver.user?.status ||
                            '—'
                          }
                          size="small"
                          color={
                            driver.user?.status === 'ACTIVE'
                              ? 'success'
                              : driver.user?.status === 'PENDING_APPROVAL'
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </TableCell>
                    )}
                    {!tableState.isColumnHidden('account') && (
                      <TableCell>
                        <Tooltip title={acctId || ''}>
                          <span>{account?.businessName || (acctId ? acctId.substring(0, 8) + '…' : '—')}</span>
                        </Tooltip>
                      </TableCell>
                    )}
                    {!tableState.isColumnHidden('rating') && (
                      <TableCell>
                        {typeof driver.user?.rating === 'number' && driver.user.rating > 0 ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Rating value={driver.user.rating} readOnly size="small" precision={0.5} />
                            <Typography variant="caption" color="text.secondary">
                              ({driver.user.ratingCount || 0})
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => openDriver(driver.id)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirm(driver)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_e, v) => setPage(v - 1)}
            color="primary"
          />
        </Box>
      )}

      {/* Driver detail dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setSelectedDriverId(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Driver Details {selectedDriver?.user?.name ? ` — ${selectedDriver.user.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDriver && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Chip
                  label={
                    getLocalizedLabel('kycVerificationStatus', selectedDriver.kycVerificationStatus || '') ||
                    selectedDriver.kycVerificationStatus ||
                    '—'
                  }
                  color={kycChipColor(selectedDriver.kycVerificationStatus)}
                  size="small"
                />
                <Chip
                  label={
                    getLocalizedLabel('userStatus', selectedDriver.user?.status || '') ||
                    selectedDriver.user?.status ||
                    '—'
                  }
                  size="small"
                  color={selectedDriver.user?.status === 'ACTIVE' ? 'success' : 'default'}
                />
                {selectedDriver.user?.rating != null && selectedDriver.user.rating > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Rating value={selectedDriver.user.rating} readOnly size="small" precision={0.5} />
                    <Typography variant="caption" color="text.secondary">
                      ({selectedDriver.user.ratingCount || 0})
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Divider />

              <PropertyView>
                <PropertySection title="Identity">
                  <PropertyField label="Driver ID" value={selectedDriver.id} mono copyable />
                  <PropertyField label="User ID" value={selectedDriver.user?.id} mono copyable />
                  <PropertyField
                    label="Account ID"
                    value={selectedDriver.accountId ?? selectedDriver.user?.accountId}
                    mono
                    copyable
                    full
                  />
                </PropertySection>
                <PropertySection title="Contact">
                  <PropertyField label="Email" value={selectedDriver.user?.email} />
                  <PropertyField label="Phone" value={selectedDriver.user?.phone} />
                </PropertySection>
                <PropertySection title="License">
                  <PropertyField label="License No" value={selectedDriver.licenseNo} />
                  <PropertyField label="License Expiry">
                    {expiryChip(selectedDriver.licenseExpiry)}
                  </PropertyField>
                </PropertySection>
                <PropertySection title="Activity">
                  <PropertyField label="KYC Verified At" value={selectedDriver.kycVerifiedAt} type="datetime" />
                  <PropertyField label="Last login" value={selectedDriver.user?.lastLoginAt} type="datetime" />
                  <PropertyField label="Created" value={selectedDriver.createdAt} type="datetime" full />
                </PropertySection>
                <SubjectDocuments subjectType="DRIVER" subjectId={selectedDriver.id} />
              </PropertyView>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedDriver && (
            <>
              <Button
                startIcon={<VerifiedUserIcon />}
                onClick={() => {
                  if (!selectedDriver) return
                  // Open KYC review dialog — let it discover existing verification or fall back to initiate
                  setKycVerificationId(undefined)
                  setKycInitiateParams({
                    subjectType: 'DRIVER',
                    subjectId: selectedDriver.id,
                    accountId: selectedDriver.accountId || '',
                  })
                  setKycDialogOpen(true)
                }}
              >
                Review KYC
              </Button>
              <Button
                startIcon={<TrendingUpIcon />}
                onClick={() => {
                  setScoreAdjustment(0)
                  setScoreReason('')
                  setScoreDialogOpen(true)
                }}
              >
                Adjust Score
              </Button>
              {selectedDriver.user?.id && (
                <Button
                  color="warning"
                  onClick={() => setResetRatingTarget(selectedDriver)}
                  disabled={resetRatingMutation.isPending}
                >
                  Reset Rating
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Score Adjustment dialog */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Adjust Driver Score</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              type="number"
              label="Adjustment (positive or negative) *"
              value={scoreAdjustment}
              onChange={(e) => setScoreAdjustment(parseInt(e.target.value, 10) || 0)}
              fullWidth
              size="small"
              inputProps={{ step: 1 }}
            />
            <TextField
              label="Reason *"
              value={scoreReason}
              onChange={(e) => setScoreReason(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !scoreAdjustment ||
              !scoreReason.trim() ||
              !selectedDriverId ||
              adjustScoreMutation.isPending
            }
            onClick={() =>
              selectedDriverId &&
              adjustScoreMutation.mutate({
                driverId: selectedDriverId,
                adjustment: scoreAdjustment,
                reason: scoreReason.trim(),
              })
            }
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* KYC review dialog */}
      <VerificationReviewDialog
        open={kycDialogOpen}
        onClose={() => {
          setKycDialogOpen(false)
          setKycVerificationId(undefined)
          setKycInitiateParams(undefined)
        }}
        verificationId={kycVerificationId}
        initiate={kycInitiateParams}
        onActionComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-drivers'] })
          queryClient.invalidateQueries({ queryKey: ['driver', selectedDriverId] })
        }}
      />

      {/* Delete confirmation (typed) */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete driver?"
        description={
          deleteConfirm ? (
            <>
              You're about to delete <strong>{deleteConfirm.user?.name || deleteConfirm.licenseNo}</strong>.
              The driver record will be removed; the underlying user account stays.
              This cannot be undone.
            </>
          ) : (
            'This cannot be undone.'
          )
        }
        confirmPhrase={deleteConfirm?.licenseNo || 'DELETE'}
        confirmHelper="Type the driver's license number to confirm."
        confirmLabel="Delete"
        severity="error"
        loading={deleteMutation.isPending}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
      />

      {/* Reset-rating confirmation (typed) */}
      <ConfirmDialog
        open={!!resetRatingTarget}
        title="Reset driver rating?"
        description={
          <>
            This sets the underlying user's <code>rating</code> to <code>null</code> and{' '}
            <code>ratingCount</code> to <code>0</code>. Existing shipment ratings remain in the
            audit log but no longer influence the aggregate. This cannot be undone.
          </>
        }
        confirmPhrase="RESET"
        confirmLabel="Reset Rating"
        severity="warning"
        loading={resetRatingMutation.isPending}
        onClose={() => setResetRatingTarget(null)}
        onConfirm={() => {
          if (resetRatingTarget?.user?.id) {
            resetRatingMutation.mutate(resetRatingTarget.user.id)
          }
          setResetRatingTarget(null)
        }}
      />
    </Box>
  )
}
